﻿import React, { useState, useEffect } from 'react';
import InstrumentInformation from './InstrumentInformation';
import InstrumentSelectBoxTooltip from './InstrumentSelectBoxTooltip';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { BenchSheetTechnologyType, ModalMessageType, VmiGroups } from '../models/enums';
import { Asset } from '../models/asset';
import { FieldUpdate } from '../models/field-update';
import { ResidueOnIgnitionBenchSheet } from '../models/residue-on-ignition-bench-sheet';
import { DataTemplates } from '../templates/data-templates';
import { CrudFunctions } from '../utilities/crud-functions';
import LogbookEntryComponent from './Common/LogBookEntryComponent';

import SelectBox from 'devextreme-react/select-box';
import Button from 'devextreme-react/button';
import { Popup as DialogPopup } from 'devextreme-react/popup';

export interface Props {
    benchSheet: ResidueOnIgnitionBenchSheet;
    readOnly?: boolean;
}

export default function ResidueOnIgnitionInstruments(props: Props) {
    const [balances, setBalances] =
        useState<Asset[]>();

    const [selectedBalance, setSelectedBalance] =
        useState<Asset>();

    const [ovens, setOvens] =
        useState<Asset[]>();

    const [selectedOven, setSelectedOven] =
        useState<Asset>();

    const [furnaces, setFurnaces] =
        useState<Asset[]>();

    const [selectedFurnace, setSelectedFurnace] =
        useState<Asset>();

    const [desiccators, setDesiccators] =
        useState<Asset[]>();

    const [selectedDesiccator, setSelectedDesiccator] =
        useState<Asset>();

    const [selectedLogbookAsset, setSelectedLogbookAsset] =
        useState<Asset>();

    const [logbookEntryDialogVisible, setLogbookEntryDialogVisible] =
        useState<boolean>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    const [dataLoaded, setDataLoaded] =
        useState<boolean>();

    useEffect(() => {
        if (!dataLoaded)
            getAllData();
    }, [props.benchSheet]);

    return (
        <div>
            <div className="lq-header pw-margin-top-05">
                <div className="lq-header-title">Instruments</div>
            </div>

            {dataLoaded && balances && furnaces && ovens && desiccators &&
                <div className="pw-form-section">
                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-20">
                            <div className="pw-form-unit-label">Balance</div>
                            <SelectBox
                                elementAttr={{ class: "balance-selectbox" }}
                                readOnly={props.readOnly}
                                placeholder='Balance'
                                className="asset-selectbox"
                                height='1.8rem'
                                itemRender={DataTemplates.InstrumentItemRender}
                                dataSource={balances ? balances : []}
                                onValueChanged={onBalanceChanged}
                                valueExpr={'assetId'}
                                displayExpr={(item) => {
                                    return item ? `${item.formattedAssetNumber} - ${item.assetName ?? "NA"}` : "";
                                }}
                                value={selectedBalance?.assetId} />
                            <InstrumentSelectBoxTooltip target=".balance-selectbox" instrument={selectedBalance} />
                        </div>

                        <div className="pw-row-button-08">
                            <Button text="Logbook" icon="edit" type="success"
                                disabled={!selectedBalance}
                                onClick={() => handleLogbookOpenClick(selectedBalance)} />
                        </div>

                        <InstrumentInformation instrument={selectedBalance} />
                    </div>

                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-20">
                            <div className="pw-form-unit-label">Microash Furnace</div>
                            <SelectBox
                                elementAttr={{ class: "furnace-selectbox" }}
                                readOnly={props.readOnly}
                                placeholder='Furnace'
                                className="asset-selectbox"
                                height='1.8rem'
                                itemRender={DataTemplates.InstrumentItemRender}
                                dataSource={furnaces ? furnaces : []}
                                onValueChanged={onFurnaceChanged}
                                valueExpr='assetId'
                                displayExpr={(item) => {
                                    return item ? `${item.formattedAssetNumber} - ${item.assetName ?? "NA"}` : "";
                                }}
                                value={selectedFurnace?.assetId} />
                            <InstrumentSelectBoxTooltip target=".furnace-selectbox" instrument={selectedFurnace} />
                        </div>

                        <div className="pw-row-button-08">
                            <Button text="Logbook" icon="edit" type="success"
                                disabled={!selectedFurnace}
                                onClick={() => handleLogbookOpenClick(selectedFurnace)} />
                        </div>

                        <InstrumentInformation instrument={selectedFurnace} />
                    </div>

                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-20">
                            <div className="pw-form-unit-label">Oven</div>
                            <SelectBox
                                elementAttr={{ class: "oven-selectbox" }}
                                readOnly={props.readOnly}
                                placeholder='Oven'
                                className="asset-selectbox"
                                height='1.8rem'
                                itemRender={DataTemplates.InstrumentItemRender}
                                dataSource={ovens ? ovens : []}
                                onValueChanged={onOvenChanged}
                                valueExpr='assetId'
                                displayExpr={(item) => {
                                    return item ? `${item.formattedAssetNumber} - ${item.assetName ?? "NA"}` : "";
                                }}
                                value={selectedOven?.assetId} />
                            <InstrumentSelectBoxTooltip target=".oven-selectbox" instrument={selectedOven} />
                        </div>

                        <div className="pw-row-button-08">
                            <Button text="Logbook" icon="edit" type="success"
                                disabled={!selectedOven}
                                onClick={() => handleLogbookOpenClick(selectedOven)} />
                        </div>

                        <InstrumentInformation instrument={selectedOven} />
                    </div>

                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-20">
                            <div className="pw-form-unit-label">Desiccator</div>
                            <SelectBox
                                elementAttr={{ class: "desiccator-selectbox" }}
                                readOnly={props.readOnly}
                                placeholder='Desiccator'
                                className="asset-selectbox"
                                height='1.8rem'
                                itemRender={DataTemplates.InstrumentItemRender}
                                dataSource={desiccators ? desiccators : []}
                                onValueChanged={onDesiccatorChanged}
                                valueExpr='assetId'
                                displayExpr={(item) => {
                                    return item ? `${item.formattedAssetNumber} - ${item.assetName ?? "NA"}` : "";
                                }}
                                value={selectedDesiccator?.assetId} />
                            <InstrumentSelectBoxTooltip target=".desiccator-selectbox" instrument={selectedDesiccator} />
                        </div>

                        <div className="pw-row-button-08">
                            <Button text="Logbook" icon="edit" type="success"
                                disabled={!selectedDesiccator}
                                onClick={() => handleLogbookOpenClick(selectedDesiccator)} />
                        </div>

                        <InstrumentInformation instrument={selectedDesiccator} />
                    </div>

                </div>
            }

            {logbookEntryDialogVisible &&
                <DialogPopup visible={logbookEntryDialogVisible}
                    onHiding={hideLogbookEntryDialog}
                    title="Logbook Entry"
                    width='80vw'
                    height='80vh'
                    dragEnabled={false}
                    hideOnOutsideClick={false}
                    resizeEnabled={false}>
                    <LogbookEntryComponent asset={selectedLogbookAsset}
                        benchSheetId={props.benchSheet.benchSheetId}
                        onClose={hideLogbookEntryDialog} />
                </DialogPopup>
            }

            {
                globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} onClose={clearGlobalDialog} />
            }

        </div >
    );

    async function getBalances() {
        await CrudFunctions.getData("assets/allbalances")
            .then(data => {
                setBalances(data);
                const selectedBalance = data.find(i => i.assetId === props.benchSheet?.balance?.assetId);
                if (selectedBalance)
                    setSelectedBalance(selectedBalance);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Balances");
            });
    }

    async function getFurnaces() {
        await CrudFunctions.getData(`assets/assets?technologyType=${BenchSheetTechnologyType.ResidueOnIgnition}&vmiGroup=${VmiGroups.MicroashFurnace}`)
            .then(data => {
                setFurnaces(data);
                const selectedFurnace = data.find(i => i.assetId === props.benchSheet?.furnace?.assetId);
                if (selectedFurnace)
                    setSelectedFurnace(selectedFurnace);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Furnaces");
            });
    }

    async function getOvens() {
        await CrudFunctions.getData(`assets/assets?technologyType=${BenchSheetTechnologyType.ResidueOnIgnition}&vmiGroup=${VmiGroups.DryingOven}`)
            .then(data => {
                setOvens(data);
                const selectedOven = data.find(i => i.assetId === props.benchSheet?.oven?.assetId);
                if (selectedOven)
                    setSelectedOven(selectedOven);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Ovens");
            });
    }

    async function getDesiccators() {
        await CrudFunctions.getData(`assets/assets?technologyType=${BenchSheetTechnologyType.ResidueOnIgnition}&vmiGroup=${VmiGroups.Desiccator}`)
            .then(data => {
                setDesiccators(data);
                const selectedDesiccator = data.find(i => i.assetId === props.benchSheet?.desiccator?.assetId);
                if (selectedDesiccator)
                    setSelectedDesiccator(selectedDesiccator);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Desiccators");
            });
    }

    async function getAllData() {
        await Promise.all([
            getBalances(),
            getFurnaces(),
            getOvens(),
            getDesiccators(),
        ])
            .then(() => setDataLoaded(true));
    }

    async function onBalanceChanged(e: any) {
        if (!e.event)
            return;

        if (balances && e.value) {
            let selectedItem = balances.find(b => b.assetId === e.value);

            if (selectedItem) {
                setSelectedBalance(selectedItem);
                let fieldUpdate: FieldUpdate = {
                    entity: "ResidueOnIgnitionBenchSheets",
                    field: "balanceId",
                    value: e.value,
                    rowId: props.benchSheet.benchSheetId
                };

                await updateField(fieldUpdate)
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating BalanceId");
                    });
            }
        }
    }

    async function onFurnaceChanged(e: any) {
        if (!e.event)
            return;

        if (furnaces && e.value) {
            let selectedItem = furnaces.find(i => i.assetId === e.value);

            if (selectedItem) {
                setSelectedFurnace(selectedItem)

                let fieldUpdate: FieldUpdate = {
                    entity: "ResidueOnIgnitionBenchSheets",
                    field: "furnaceId",
                    value: e.value,
                    rowId: props.benchSheet.benchSheetId
                };

                await updateField(fieldUpdate)
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating FurnaceId");
                    });
            }
        }
    }

    async function onOvenChanged(e: any) {
        if (!e.event)
            return;

        if (ovens && e.value) {
            let selectedItem = ovens.find(i => i.assetId === e.value);

            if (selectedItem) {
                setSelectedOven(selectedItem)

                let fieldUpdate: FieldUpdate = {
                    entity: "ResidueOnIgnitionBenchSheets",
                    field: "ovenId",
                    value: e.value,
                    rowId: props.benchSheet.benchSheetId
                };

                await updateField(fieldUpdate)
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating OvenId");
                    });
            }
        }
    }

    async function onDesiccatorChanged(e: any) {
        if (!e.event)
            return;

        if (desiccators && e.value) {
            let selectedItem = desiccators.find(i => i.assetId === e.value);

            if (selectedItem) {
                setSelectedDesiccator(selectedItem)

                let fieldUpdate: FieldUpdate = {
                    entity: "ResidueOnIgnitionBenchSheets",
                    field: "desiccatorId",
                    value: e.value,
                    rowId: props.benchSheet.benchSheetId
                };

                await updateField(fieldUpdate)
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating DesiccatorId");
                    });
            }
        }
    }

    async function updateField(fieldUpdate: FieldUpdate) {
        return CrudFunctions.putData("benchsheets/roibenchsheetfield", fieldUpdate);
    }


    function handleLogbookOpenClick(asset) {
        if (!asset) return;
        setSelectedLogbookAsset(asset);
        setLogbookEntryDialogVisible(true);
    }

    function hideLogbookEntryDialog() {
        setLogbookEntryDialogVisible(false);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function clearGlobalDialog() {
        setGlobalDialogMetadata(null);
        setGlobalDialogVisible(false);
    }

}
